/**
 * **The command center starts over on a reseed.** Mounted around `/command` in
 * App.tsx.
 *
 * The command center diffs every poll against the shifts it already holds and
 * reprices the drive times of any shift that changed (`reduceDriveUpdatesNeeded`
 * on `SET_DB_RESOURCES`). Reset database replaces every shift row, so the poll
 * after a reset read every shift in view as changed: one paid drive lookup per
 * shift, and the results left on screen as unsaved edits. A fresh mount holds
 * no shifts and trusts the drive times it loads, which is why a page refresh
 * did not do it (Pete, 2026-10-13).
 *
 * So the command center is keyed by the seed's stamp, which changes exactly
 * when the tenant under it was reseeded, and remounts instead of diffing. Off
 * the demo org it renders its children and nothing else.
 */
import { Fragment, type ReactNode } from "react";
import { useDemoSelector } from "../store/useDemoStore";
import { useHomeHealthDemoEnabled } from "../useHomeHealthDemoEnabled";

function KeyedBySeed({ children }: { children: ReactNode }) {
  const stamp = useDemoSelector((s) => s.meta.tenantStamp);
  return <Fragment key={stamp ?? "unseeded"}>{children}</Fragment>;
}

export function FreshOnReseed({ children }: { children: ReactNode }) {
  return useHomeHealthDemoEnabled() ? <KeyedBySeed>{children}</KeyedBySeed> : <>{children}</>;
}
